<script>
import { Maps } from "tortilla";
import mapStylesDefault from "../vendor/google-map-snazzymaps";
import ComplexPagination from "./complex-pagination";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { createMap, createMarker, fitMapBounds, createInfoWindow } from '../utils/mapUtils';

const kebabCase = string => string
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_\.]+/g, '-')
    .toLowerCase();

const removeDuplicatesByKey = (arr, key) => {
    let seen = {};
    return arr.filter(item => {
        if (seen[item[key]]) {
            return false;
        }
        seen[item[key]] = true;
        return true;
    });
}

export default {
    name: 'MoveInReady',
    components: {
        ComplexPagination,
    },
    props: {
        homes: {
            type: Array,
            default: () => ([])
        },
    },
    data: () => ({
        sortByList: [
            {key: 'availability-ascending', value: 'Availability: Soonest'},
            {key: 'availability-descending', value: 'Availability: Latest'},
            {key: 'price-ascending', value: 'Price: Low to High'},
            {key: 'price-descending', value: 'Price: High to Low'},
            {key: 'sqft-ascending', value: 'Sq. Ft: Low to High'},
            {key: 'sqft-descending', value: 'Sq. Ft: High to Low'},
            {key: 'beds-ascending', value: 'Beds: Low to High'},
            {key: 'beds-descending', value: 'Beds: High to Low'},
            {key: 'baths-ascending', value: 'Baths: Low to High'},
            {key: 'baths-descending', value: 'Baths: High to Low'},
            // {key: 'garage-ascending', value: 'Garages: Low to High'},
            // {key: 'garage-descending', value: 'Garages: High to Low'},
        ],
        filters: {
            metro: '',
            city: '',
            community: '',
            beds: '',
            baths: '',
            garages: '',
            priceMin: '0',
            priceMax: '',
            is_bold_series_home: '',
        },
        sortBy: 'price-ascending',
        markers: [],
        map: undefined,
        gm: undefined,
        isMapModalActive: false,
        selectedHome: undefined,
        markerClusterer: undefined,
        mapStyles: mapStylesDefault,
        activeItem: window.innerWidth > 768 ? 'map' : 'list',
        pageSize: 10,
        page: 1,
        hoveredHomeId: 0,
        observer: undefined,
        isAutoScrollAllowed: window.innerWidth > 768,
        isFilteringByMarkersInView: false,
        mapBoundary: window.innerWidth >= 768
            ? (window.innerWidth >= 992
                ? { top: 108, right: 0, bottom: 0, left: 0 }
                : { top: 80, right: 0, bottom: 0, left: 0 })
            : { top: 80, right: 0, bottom: 200, left: 0 },
        visibleMarkers: [],
        filtersPanel: false,
        currencyFormatter: new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0}),
        activeDropdown : '',
        searchParams: new URLSearchParams(window.location.search),
        loaded: false,
    }),
     created() {
        this.filters.priceMin = this.computedPriceMin;
        this.filters.priceMax = this.computedPriceMax;
        this.addQueryInFilters()
    },
    computed: {
        computedLocationFilterCount() {
            let count = 0;
            if (this.filters.community) {
                count++;
            }
            if (this.filters.city) {
                count++;
            }
            if (this.filters.metro) {
                count++;
            }
            return count;
        },
        computedPriceFilterCount() {
            let count = 0;
            if (this.filters.priceMin != this.computedPriceMin) {
                count++;
            }
            if (this.filters.priceMax != this.computedPriceMax) {
                count++;
            }
            return count;
        },
        computedSnapshotFilterCount() {
            let count = 0;
            if (this.filters.beds) {
                count++;
            }
            if (this.filters.baths) {
                count++;
            }
            if (this.filters.garages) {
                count++;
            }
            return count;
        },
        openModal() {
            return this.$parent.openModal;
        },
        optionsMetros() {
            // return [...new Set(this.homes.filter(home => home.metro_name !== null).map(home => ({key: home.metro_seo_name, value: home.metro_name })))]
            //     .map(o => ({ key: o.key, value: o.value }))
            //     .sort((a, b) => a.value.localeCompare(b.value));
            const uniqueMetros = removeDuplicatesByKey(this.homes.filter(home => home.metro_name !== null).map(home => ({key: home.metro_seo_name, value: home.metro_name })), 'key');
            return uniqueMetros
                .map(o => ({ key: o.key, value: o.value }))
                .sort((a, b) => a.value.localeCompare(b.value));
        },
        optionsCities() {
            return [...new Set(this.homes.filter(home => !this.filters.metro || home.metro_name !== null && kebabCase(home.metro_name) === this.filters.metro).map(home => home.city))]
                .map(o => ({ key: kebabCase(o), value: o }))
                .sort((a, b) => a.value.localeCompare(b.value));
        },
        optionsCommunities() {
            return this.homes
                .filter(home => !this.filters.metro || home.metro_name !== null && kebabCase(home.metro_name) === this.filters.metro)
                .filter(home => !this.filters.city || kebabCase(home.city) === this.filters.city)
                .reduce((homes, home) => {
                    const homeExists = homes.find(h => h.key === home.community_id);

                    if (!homeExists) {
                        homes.push({
                            key: home.community_id,
                            value: home.community_name,
                            seoName: home.community_seo_name,
                            cityKey: kebabCase(home.city)
                        });
                    }
                    return homes;
                }, [])
                .sort((a, b) => a.value.localeCompare(b.value));
        },
        optionsFloorplans() {
            let ids = [];
            return this.homes
                .map(h => ({id: h.residence_id, name: h.name }))
                .reduce((result, item) => {
                    if (!ids.includes(item.id)) {
                        ids.push(item.id);
                        result.push({ key: item.id, value: item.name });
                    }
                    return result;
                }, [])
                .sort((a, b) => a.value.localeCompare(b.value));
        },
        optionsBeds() {
            let beds = this.homes
            .map(h => h.beds)
            const bedsMin = Math.min(...beds);
            const bedsMax = Math.max(...beds) + 1;
            const options = [];
            for(let i = bedsMin; i < bedsMax; i++) {
                let plus = (i >= bedsMax - 1) ? '' : '+';
                options.push({key: i, value: i + plus})
            }
            return options;
        },
        optionsBaths() {
            let baths = this.homes
            .map(h => h.baths)
            const bathsMin = Math.min(...baths);
            const bathsMax = Math.max(...baths) + 1;
            const options = [];
            for(let i = bathsMin; i < bathsMax; i+=0.5) {
                let plus = (i >= bathsMax - 1) ? '' : '+';
                options.push({key: i, value: i + plus})
            }
            return options;
        },
        optionsGarages() {
            let garages = this.homes
            .map(h => h.garage)
            const garagesMin = Math.min(...garages);
            const garagesMax = Math.max(...garages) + 1;
            const options = [];
            for(let i = garagesMin; i < garagesMax; i++) {
                let plus = (i >= garagesMax -1) ? '' : '+';
                options.push({key: i, value: i + plus})

            }
            return options;
        },
        filteredHomes() {
            return this.homes.filter(this.filterHome).sort(this.sortHome);
        },
        paginatedHomes() {
            const filteredHomes = this.isFilteringByMarkersInView
                ? this.filteredHomes.filter(this.filterByMarkerInView)
                : this.filteredHomes;

            const paginatedHomes = filteredHomes.reduce((acc, val, i) => {
                let idx = Math.floor(i / this.pageSize);
                let page = acc[idx] || (acc[idx] = []);
                val.page = idx + 1;
                page.push(val);
                return acc;
            }, [])

            return paginatedHomes;
        },
        pageCount() {
            return this.paginatedHomes.length;
        },
        currentPage() {
            return this.paginatedHomes[this.page - 1];
        },
        computedPriceMin() {
            return Math.floor(Math.min(...this.homes.filter(c => c.price > 0).map(c => c.price)) / 1000) * 1000;
        },
        computedPriceMax() {
            return Math.ceil(Math.max(...this.homes.map(c => c.price)) / 1000) * 1000;
        },
        sort() {
            return this.sortBy ? this.sortBy.split('-')[0] : null;
        },
        sortDirection() {
            return this.sortBy ? this.sortBy.split('-')[1] : null;
        }
    },
    watch: {
        'filters.metro': {
            handler() {
                this.filters.city = '';
                this.filters.community = '';
            }
        },
        'filters.city': {
            handler() {
                this.filters.community = '';
            }
        },
        filters: {
            deep: true,
            handler: function(newFilters) {
                if(this.gm) {
                    this.updateFilters();
                }
                this.setQueryParams(newFilters);
            },
        },
        hoveredHomeId(id) {
            if (id && this.isAutoScrollAllowed) {
                this.findRelatedCard(id);
            }
        },
        computedPriceMin(val) {
            this.filters.priceMin = val;
        },
        computedPriceMax(val) {
            this.filters.priceMax = val;
        },
    },
    mounted() {
        events.$on("map-intialized", (map) => {
            this.initialize(map);
        });

        this.observer = new ResizeObserver(this.handleResizeObservation);
        this.observer.observe(this.$el);
    },
    beforeDestroy() {
        this.observer.disconnect();
    },
    methods: {
        setActiveDropdown(ref) {
            if(this.activeDropdown === ref) {
                this.activeDropdown = '';
            } else {
                this.activeDropdown = ref;
            }

        },
        isDropdownActive(ref) {
            return this.activeDropdown === ref;
        },
        closeDropdown() {
            this.activeDropdown = '';
        },
        clearFilters(...filters) {
            if(filters) {
                filters.forEach(filter => {
                    if(filter == 'price') {
                        this.filters.priceMin = this.computedPriceMin;
                        this.filters.priceMax = this.computedPriceMax;
                    } else {
                        this.filters[filter] = '';
                    }
                })
            }
        },
        handleResizeObservation(entries) {
            for (let entry of entries) {
                const width = entry.contentBoxSize
                    ? (Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0].inlineSize : entry.contentBoxSize.inlineSize)
                    : entry.contentRect.width;

                this.isAutoScrollAllowed = width > 768;
                this.isFilteringByMarkersInView = width > 768;

                if (width <= 768) { // mobile
                    this.mapBoundary = { top: 80, right: 0, bottom: 0, left: 0 };
                } else if (width <= 992) { // tablet
                    this.mapBoundary = { top: 80, right: 0, bottom: 0, left: 0 };
                } else { // desktop+
                    this.mapBoundary = { top: 108, right: 0, bottom: 0, left: 0 };
                }
            }
        },
        handleMapChange() {
            const mapBounds = this.map.getBounds();
            this.visibleMarkers = this.filteredHomes
                .filter((home) => {
                    const latitude = parseFloat(home.latitude);
                    const longitude = parseFloat(home.longitude);
                    return !(latitude && longitude);
                }).map(home => home.id);

            for (let marker of this.markers) {
                // const position = marker.getPosition();

                const position = new google.maps.LatLng(parseFloat(marker.content.getAttribute("lat")), parseFloat(marker.content.getAttribute("lng")));

               const isInBounds = mapBounds.contains(position)
                if (isInBounds) {
                    const homeId = parseInt(marker.content.getAttribute("id").split("_")[1]);
                    this.visibleMarkers.push(homeId)
                    // console.log("isInBounds",isInBounds);
                    // console.log("marker",marker);
                    // this.visibleMarkers.push(marker.homeId);
                }
            }
        },
        filterByMarkerInView(home) {
            return this.visibleMarkers.includes(home.id);
        },
        isActive: function (menuItem) {
            return this.activeItem === menuItem;
        },
        setActive: function (menuItem) {
            this.activeItem = menuItem;
        },
        toggleFiltersPanel: function() {
            this.filtersPanel = !this.filtersPanel;
            if (this.filtersPanel) {
                document.body.classList.add('mir--panel-open');
            } else {
                document.body.classList.remove('mir--panel-open');
            }
        },
        filterHome(home) {
            let passesFilter = true;

            if (this.filters.metro && (home.metro_name === null || (home.metro_seo_name && kebabCase(home.metro_seo_name) !== this.filters.metro))) {
                passesFilter = false;
            }

            if (passesFilter && this.filters.city && kebabCase(home.city) !== this.filters.city) {
                passesFilter = false;
            }

            if (passesFilter && this.filters.community && home.community_id !== this.filters.community) {
                passesFilter = false;
            }

            if (passesFilter && this.filters.priceMin && parseInt(this.filters.priceMin, 10) !== this.computedPriceMin) {
                if (home.price < parseInt(this.filters.priceMin, 10)) {
                    passesFilter = false;
                }
            }

            if (passesFilter && this.filters.priceMax && parseInt(this.filters.priceMax, 10) !== this.computedPriceMax) {
                if (home.price > parseInt(this.filters.priceMax, 10)) {
                    passesFilter = false;
                }
            }

            if (passesFilter && this.filters.beds && parseInt(this.filters.beds) > parseInt(home.beds)) {
                passesFilter = false;
            }

            if (passesFilter && this.filters.baths && parseFloat(this.filters.baths) > parseFloat(home.baths)) {
                passesFilter = false;
            }

            if (passesFilter && this.filters.garages && parseInt(this.filters.garages) > parseInt(home.garage)) {
                passesFilter = false;
            }

            if (passesFilter && this.filters.is_bold_series_home && !!home.is_bold_series_home !== this.filters.is_bold_series_home) {
                passesFilter = false;
            }

            return passesFilter;
        },
        sortHomeByDate(homeA, homeB, sortDirection) {
            if (!homeA.availability_date && !homeB.availability_date) {
                return 0;
            }
            if (!homeA.availability_date) {
                return 1;
            }
            if (!homeB.availability_date) {
                return -1;
            }
            return sortDirection === 'ascending'
                ? new Date(homeA.availability_date) - new Date(homeB.availability_date)
                : new Date(homeB.availability_date) - new Date(homeA.availability_date);
        },

        /**
         * Sorts 2 homes
         * @param {Object} homeA - The first home item to compare
         * @param {Object} homeB - The second home item to compare
         * @returns {Array} sorted items
         */
        sortHome(homeA, homeB) {
            const sort = this.sort;

            if(sort === 'availability') {
                return this.sortHomeByDate(homeA, homeB, this.sortDirection);
            }
            let a = homeA[sort] ? parseFloat(homeA[sort]) : 0;
            let b = homeB[sort] ? parseFloat(homeB[sort]) : 0;

            // If homes are equal, sort secondarily by price unless
            // primary sort is price
            if (sort !== 'price' && a === b) {
                a = homeA.price ? parseFloat(homeA.price) : 0;
                b = homeB.price ? parseFloat(homeB.price) : 0;
                // Secondary price sorting will always be ascending
                return a === 0 ? 1 : (b === 0 ? -1 : a - b);
            }

            // Put all 0 or empty properties last
            if (this.sortDirection === 'ascending' && a === 0) {
                return 1;
            }
            if (this.sortDirection === 'ascending' && b === 0) {
                return -1;
            }
            if (this.sortDirection === 'descending' && a === 0) {
                return -1;
            }
            if (this.sortDirection === 'descending' && b === 0) {
                return 1;
            }

            return this.sortDirection === 'ascending' ? a - b : b - a;
        },
        resetFilters() {
            this.filters.metro = '';
            this.filters.city = '';
            this.filters.community = '';
            this.filters.price = '';

            this.filters.priceMin = this.computedPriceMin;
            this.filters.priceMax = this.computedPriceMax;
            this.filters.beds = '';
            this.filters.baths = '';
            this.filters.garages = '';
            this.filters.is_bold_series_home = '';
            this.sortBy = 'price-ascending';
        },
       async initialize(map) {
            try {
                const options = {
                    zoom: 8,
                    center: {
                        lat: 27.876322,
                        lng: -82.329134,
                    },
                }
                this.gm = google.maps;
                this.map = await createMap('move-in-ready-map', options);
                this.infoWindow = createInfoWindow();
                this.updateMarkers();
                this.initListeners();
                this.initClusterer();

                    this.loaded = true;

            } catch (error) {
                console.log("error init map: ",error);
            }
        },
         initListeners() {
            this.map.addListener('zoom_changed', this.handleMapChange);
            this.map.addListener('center_changed', this.handleMapChange);
            this.map.addListener('bounds_changed', this.handleMapChange);
        },
         initClusterer() {
            this.markerClusterer = new MarkerClusterer({
                markers: this.markers,
                map: this.map,
                renderer: {
                    render: ({ count, position }) => {
                        const marker = new google.maps.Marker({
                            position,
                            icon: {
                                url: '/images/site/maps/cluster-map-pin.svg',
                                scaledSize: new google.maps.Size(60, 45),
                                labelOrigin: new google.maps.Point(27, 20)
                            },
                            label: {
                                text: String(count),
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '400',
                            }
                        });
                        return marker;
                    }
                }
            });
            fitMapBounds(this.map, this.markers, 13);
            console.log("this.markerClusterer", this.markerClusterer);
        },
        goToPage(p) {
            this.page = p;
        },
        findRelatedCard(id) {
            const page = this.paginatedHomes.findIndex(homes => homes.find(h => h.id == id));
            this.goToPage(page + 1);
            requestAnimationFrame(() => {
                const card = this.$refs[`casaCard${id}`] ? this.$refs[`casaCard${id}`][0] : null;
                if (card) {
                    card.scrollIntoView({
                        inline: 'nearest',
                        block: 'center',
                        behavior: 'smooth'
                    });
                }
            });
        },
        /**
         * Updates the filters on all items
         */
        updateFilters() {
            if (this.gm) {
                this.$nextTick(this.clearMarkers);
            }
        },
        clearMarkers() {
            this.markers = [];
            this.markerClusterer.clearMarkers();
            this.updateMarkers();
            this.$nextTick(this.updateMarkers);
        },
        async updateMarkers() {
            this.markers = await Promise.all(this.filteredHomes.filter((home) => {
                const latitude = parseFloat(home.latitude);
                const longitude = parseFloat(home.longitude);
                const hasCoordinates = !!(latitude && longitude);
                if (!hasCoordinates) {
                    this.visibleMarkers.push(home.id);
                }
                return hasCoordinates;
            }).map(async (hm) => {
                return await this.generateMarker(hm);
            }));
            this.clusterMarkers();

            fitMapBounds(this.map, this.markers, 13);
        },
        clusterMarkers() {
            if (this.markerClusterer) {
                this.markerClusterer.clearMarkers();
                this.markerClusterer.addMarkers(this.markers);
            }
        },
        async  generateMarker(home) {
            const latitude = parseFloat(home.latitude);
            const longitude = parseFloat(home.longitude);

            var marker = await createMarker({
                map: this.map,
                position: {
                    lat: latitude,
                    lng: longitude
                },
                icon:'/images/site/maps/mir-map-pin.svg',
                title: home.address,
                className: 'custom-marker',
                id: `marker_${home.id}`
            });

            marker.addListener('click', () => {
                marker.content.style.zIndex = this.gm.Marker.MAX_ZINDEX + 1;
                this.openMapModal(home, marker);
            });

            marker.content.addEventListener('mouseover', () => {
                this.hoveredHomeId = home.id;
            });
            marker.content.addEventListener('mouseout', () => {
                this.hoveredHomeId = 0;
            });

            fitMapBounds(this.map, this.markers, 13);

            return marker;
        },
        openMapModal(home, marker) {
            this.selectedHome = home;
            this.selectedMarker = marker;
            this.isMapModalActive = true;
            this.trapFocusInsideModal();
        },
        closeMapModal(item) {
            if (this.selectedMarker) {
                this.selectedMarker.content.style.zIndex = this.gm.Marker.MAX_ZINDEX;
            }
            this.selectedHome = null;
            this.selectedMarker = null;
            this.isMapModalActive = false
            document.removeEventListener('keydown', this.handleKeyDown, false);
        },

        trapFocusInsideModal() {
            // this.$nextTick(() => {
            //     const focusableElems = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
            //     const modal = document.querySelector(`.map-modal__content`);
            //     this.focusableContent = modal.querySelectorAll(focusableElems);
            //     this.firstFocusableElem = this.focusableContent[0];
            //     this.lastFocusableElem = this.focusableContent[this.focusableContent.length - 1];
            //     document.addEventListener('keydown', this.handleKeyDown, false);
            //     this.firstFocusableElem.focus();
            // });
        },
        handleKeyDown(e) {
            let isTabPressed = e.key === 'Tab' || e.keyCode === 9;
            if (e.key === 'Escape' || e.keyCode === 27) {
                this.closeMapModal();
            }
            if (!isTabPressed) {
                return;
            }
            if (e.shiftKey) {
                if (document.activeElement.isEqualNode(this.firstFocusableElem)) {
                    this.lastFocusableElem.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement.isEqualNode(this.lastFocusableElem)) {
                    this.firstFocusableElem.focus();
                    e.preventDefault();
                }
            }
        },

        handlePageChange: function(page) {
            this.page = page;
        },
        /**
         * Formatter for the price range filter
         * @param {string} val - The string to format
         */
        priceFormat(val) {
            return val ? this.currencyFormatter.format(val) + 'k' : val;
        },

        recenterMap() {
           this.updateFilters();
        },
        setQueryParams(filters){

            Object.entries(filters).forEach(([key, val]) => {
                if (val) {
                    this.searchParams.set(key, val);

                } else {
                    this.searchParams.delete(key);
                }
            });

            const url = new URL(window.location);
            if(filters.community !== ''){
                const community = this.optionsCommunities.find(({key}) => key === filters.community)
                url.searchParams.set("community", community.seoName);
                url.searchParams.delete("metro");
                url.searchParams.delete("city");
            }else if(filters.city !== ''){
                url.searchParams.set("city", filters.city);
                url.searchParams.delete("community");
                url.searchParams.delete("metro");
            }else if(filters.metro !== ''){
                url.searchParams.set("metro", filters.metro);
                url.searchParams.delete("community");
                url.searchParams.delete("city");
            }else {
                url.searchParams.delete("metro");
                url.searchParams.delete("city");
                url.searchParams.delete("community");
            }
            window.history.pushState({}, "", url);
        },
        addQueryInFilters(){
            for (const [key, value] of this.searchParams.entries()) {
                if( key === "community"){
                    const community = this.optionsCommunities.find(({seoName}) => seoName === value)
                    this.filters[key] =  community.key
                }else {
                    this.filters[key] =  value;
                }
            }
        }
    }
};
</script>

<style>
#move-in-ready-map {
    /* width: 100%; */
    height: 100%;
    /* max-height: 60vh; */
}
</style>


// WEBPACK FOOTER //
// resources/components/apps/move-in-ready.vue